'use client';

import { getSubscription } from '@/api/usage';
import dynamic from 'next/dynamic';
import { useClientEntitlement } from '@/app/hooks/use-entitlement-gate';
import { isPrepaidPlan } from '@/app/lib/billing';
import Spinner from '@/app/components/ui/Spinner';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@/app/components/ui/Button';
import { X } from 'lucide-react';

type CreditAccessState = {
  isLoading: boolean;
  isExhausted: boolean;
  remainingCredits: number | null;
};

const BuyCreditsPanel = dynamic(
  () =>
    import('@/app/(protected)/[clientId]/billing/AddCreditsModal').then(
      module => module.BuyCreditsPanel
    ),
  { ssr: false }
);

const CreditAccessContext = createContext<CreditAccessState>({
  isLoading: false,
  isExhausted: false,
  remainingCredits: null,
});

export function useCreditAccess() {
  return useContext(CreditAccessContext);
}

export function useClientCreditAccess(clientId?: string): CreditAccessState {
  const subscriptionQuery = useQuery({
    queryKey: ['credit-access-subscription', clientId],
    queryFn: () => getSubscription(clientId || ''),
    enabled: !!clientId,
  });

  return useMemo(() => {
    const subscription = subscriptionQuery.data;
    const usage = subscription?.credit_usage ?? subscription?.last_cycle_credit_usage;
    const remainingCredits = usage?.remaining_credits ?? null;

    return {
      isLoading: subscriptionQuery.isLoading,
      // Fail open for missing/error responses. The API remains the source of truth for
      // enforcing credit-consuming operations.
      isExhausted:
        !!subscription &&
        isPrepaidPlan(subscription.plan) &&
        remainingCredits !== null &&
        remainingCredits <= 0,
      remainingCredits,
    };
  }, [subscriptionQuery.data, subscriptionQuery.isLoading]);
}

function isAllowedHeaderTarget(target: EventTarget | null) {
  const element = target instanceof Element ? target : null;

  return Boolean(
    element?.closest(
      '[data-app-header], [data-radix-popper-content-wrapper], [role="menu"], [role="dialog"]'
    )
  );
}

export default function CreditAccessGate({ children }: { children: React.ReactNode }) {
  const { clientId } = useParams<{ clientId: string }>();
  const creditAccess = useClientCreditAccess(clientId);
  const { data: entitlement, isLoading: isEntitlementLoading } = useClientEntitlement(clientId);
  const t = useTranslations('creditGate');
  const canBuyCredits = entitlement?.user_permission?.billing_info?.update !== false;
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    setIsDismissed(false);
  }, [clientId]);

  if (creditAccess.isLoading || isEntitlementLoading) {
    return (
      <div className="relative h-full w-full">
        <Spinner />
      </div>
    );
  }

  if (!creditAccess.isExhausted || isDismissed) {
    return (
      <CreditAccessContext.Provider value={creditAccess}>{children}</CreditAccessContext.Provider>
    );
  }

  return (
    <CreditAccessContext.Provider value={creditAccess}>
      <div className="relative h-full w-full overflow-hidden">
        <div
          className="h-full w-full overflow-hidden [&_[data-app-header]]:z-30 [&>div>*:not([data-app-header])]:pointer-events-none [&>div>*:not([data-app-header])]:select-none [&>div>*:not([data-app-header])]:blur-[5px]"
          onClickCapture={event => {
            if (isAllowedHeaderTarget(event.target)) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onKeyDownCapture={event => {
            if (isAllowedHeaderTarget(event.target)) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onSubmitCapture={event => {
            if (isAllowedHeaderTarget(event.target)) return;
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {children}
        </div>
        <div className="absolute inset-x-0 bottom-0 top-14 z-20 flex items-start justify-center overflow-y-auto bg-background/45 p-4 sm:items-center">
          {canBuyCredits ? (
            <BuyCreditsPanel
              clientId={clientId}
              onSuccess={() => setIsDismissed(true)}
              onClose={() => setIsDismissed(true)}
              className="my-auto max-h-[calc(100dvh-88px)] max-w-[768px]"
            />
          ) : (
            <section className="relative my-auto flex w-full max-w-[480px] flex-col items-center rounded-[10px] border border-border bg-card px-8 py-10 text-center shadow-lg">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="absolute right-3 top-3 z-10 text-muted-foreground hover:text-foreground"
                onClick={() => setIsDismissed(true)}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
              <h1 className="text-xl font-semibold text-foreground">{t('title')}</h1>
              <p className="mt-2 max-w-md text-sm leading-5 text-muted-foreground">
                {t('askAdminDescription')}
              </p>
            </section>
          )}
        </div>
      </div>
    </CreditAccessContext.Provider>
  );
}
